import * as React from 'react';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// =============================================================================
// sheet.tsx — Volet latéral (drawer) accessible (R22-FICHE-DRAWER).
// =============================================================================
// Modale légère écrite à la main, SUR LE MODÈLE de `confirm-dialog.tsx` (aucune dépendance
// ajoutée — pas de @radix-ui/react-dialog dans le lockfile figé). Diffère de ConfirmDialog par
// le POSITIONNEMENT (volet ancré à droite, pleine hauteur) et par le fait qu'il enveloppe un
// contenu ARBITRAIRE (ex. la fiche fournisseur existante) plutôt qu'un titre/description fixes.
//
// Conforme WCAG 2.1 AA :
//   - role="dialog" + aria-modal="true" + aria-label (le contenu porte ses propres <h1>/<h2>) ;
//   - focus déplacé sur le panneau à l'ouverture, restauré sur l'élément précédemment focus à la
//     fermeture ;
//   - fermeture clavier (Échap) ; le fond (overlay) est décoratif (aria-hidden) et ferme au clic.
//
// IMPORTANT : le composant APPELANT (ex. suppliers-list-screen.tsx) reste monté SOUS le Sheet —
// celui-ci n'est qu'un OVERLAY superposé (position fixed), il ne démonte JAMAIS le contenu qui
// l'entoure. C'est la propriété recherchée par R22-FICHE-DRAWER (liste visible en arrière-plan).

export interface SheetProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Libellé accessible du volet (annoncé par les lecteurs d'écran ; le contenu porte ses titres propres). */
  readonly label: string;
  readonly children?: React.ReactNode;
}

export function Sheet({
  open,
  onClose,
  label,
  children,
}: SheetProps): React.JSX.Element | null {
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const previouslyFocused = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused.current?.focus();
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Overlay décoratif : ferme au clic, inerte pour les lecteurs d'écran. */}
      <div
        className="absolute inset-0 bg-foreground/40"
        aria-hidden="true"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        className={cn(
          'relative z-10 flex h-full w-full max-w-2xl flex-col overflow-y-auto',
          'border-l border-border bg-background p-6 shadow-lg',
          'focus-visible:outline-none',
        )}
      >
        <div className="mb-4 flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label="Fermer le volet"
          >
            <X aria-hidden="true" className="h-4 w-4" />
            <span className="ml-1">Fermer</span>
          </Button>
        </div>
        {/* `<section>` = CONTENU DE SECTIONNEMENT (HTML-AAM) : place le contenu (ex. la fiche
            fournisseur, qui porte son PROPRE <header>) hors du calcul de landmark « banner » —
            sans quoi un <header> top-level dans le volet entrerait en collision avec le <header>
            de la page derrière (deux landmarks « banner » = violation axe landmark-unique /
            landmark-no-duplicate-banner). Le contenu réutilisé n'est PAS modifié pour autant. */}
        <section>{children}</section>
      </div>
    </div>
  );
}
